import { useRef, useState, useEffect } from "react";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { Slide, ToastContainer, toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { DefaultLayout } from "../../../../layouts/Default";
import { Main } from "../../../../themes/Default/components/Main";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { Title } from "../../../../themes/Default/components/Title";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../themes/Default/components/Breadcrumps/components/Link";
import { Button, ButtonBack } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { Document } from "../../../../themes/Default/components/Form/components/Document";
import { Documents as DocumentsWrapper } from "../../../../themes/Default/components/Documents";
import { Item } from "../../../../themes/Default/components/Documents/components/Item";
import { Form } from "../../../../themes/Default/components/Form";
import { Row } from "../../../../themes/Default/components/Form/components/Row";
import { Field } from "../../../../themes/Default/components/Form/components/Row/components/Field";
import { groupsManagement } from "../../../../hooks/api/GroupsManagement";
import Toaster from "../../../../themes/Default/components/Toaster/Toaster";
import { useUserToken } from "../../../../hooks/core/UserToken";
import { roles } from "../../../../services/utils/Roles";
const Documents = () => {
    const formRef = useRef(null);
    const { groupId } = useParams();
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const [documents, setDocuments] = useState([]);
    const { useViewGroupMutation, useAddDocumentMutation, useListDocumentsMutation, useDeleteDocumentMutation, useDownloadDocumentMutation } = groupsManagement;
    const [addDocument] = useAddDocumentMutation();
    const [listDocuments] = useListDocumentsMutation();
    const [deleteDocument] = useDeleteDocumentMutation();
    const [downloadDocument] = useDownloadDocumentMutation();
    const [viewGroup] = useViewGroupMutation();
    const [groupName, setGroupName] = useState("");
    const [hasTests, setHasTests] = useState(false);
    const [hasProgress, setHasProgress] = useState(false);
    const [hasReport, setHasReport] = useState(false);
    const [hasPlan, setHasPlan] = useState(false);

    const tabs = [
        { title: t('groups.attendance'),onClick: () => gotoAttendanceTab(groupId)  },
        ...(hasPlan ? [
            { title: t('groups.planning'), onClick: () => gotoPlanningTab(groupId)},
        ] : []), 
        ...(hasProgress ? [
            { title: t('groups.progress'), onClick: () => gotoProgressTab(groupId)},
        ] : []), 
        ...(hasTests ? [
            { title: t('groups.progressTest'), onClick: () => gotoProgressTestsTab(groupId) }
        ] : []), 
        ...(hasReport ? [
            { title: t('reports.title'),onClick: () => gotoReportTab(groupId)},
        ] : []),
        { title: t('documents'), isActive: true },
        { title: t('notes'), onClick: () => gotoNotesTab(groupId)},
    ];

    const [{ getDecoded }] = useUserToken();
    const decodedToken = getDecoded();
    if (
        decodedToken?.roles.includes(roles.SUPER_ADMIN) ||
        decodedToken?.roles.includes(roles.SECRETARY) ||
        decodedToken?.roles.includes(roles.DIRECTOR) || 
        decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES)
    ) {
        tabs.push({
            title: t("settings.title"),
            onClick: () => gotoEditTab(groupId),
        });
    }

    const gotoPlanningTab = (groupId) => {
        navigate("/groups/" + groupId + "/planning");
    }

    const gotoProgressTestsTab = (groupId) => {
        navigate("/groups/" + groupId + "/tests");
    }

    const gotoReportTab = (groupId) => {
        navigate("/groups/" + groupId + "/reports");
    }

    const gotoNotesTab = (groupId) => {
        navigate("/groups/" + groupId + "/notes");
    }

    const gotoProgressTab = (groupId) => {
        navigate("/groups/" + groupId + "/progress");
    }

    const gotoAttendanceTab = (groupId) => {
        navigate("/groups/" + groupId + "/attendance");
    }

    const gotoEditTab = (groupId) => {
        navigate("/groups/edit/" + groupId);
    }

    const onDocumentDelete = (event, documentId) => {
        event.preventDefault();
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        deleteDocument({ groupId, documentId }).then((result) => {
                             if (result?.error) {
                                toast.error(result.error.data.error.responseMessage);
                            } else {
                                toast.info(result?.data?.info?.responseMessage);
                                viewDocuments();
                            }
                        });
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    const onDocumentDownload = async (event, documentId) => {
        event.preventDefault();
        const blob = await downloadDocument({ groupId, documentId }).unwrap();

        const docToDownload = documents.find((doc) => doc.id === documentId);
        let fileName = docToDownload?.fileName ?? "dokument";
        if (docToDownload?.extension) {
            fileName += '.' + docToDownload.extension;
        }

        const urlBlob = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = urlBlob;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(urlBlob);
    };

    const viewDocuments = () => {
        listDocuments(groupId).then((result) => {
            const documents = result?.data?.records;
            setDocuments(documents);
        });
    };

    const loadGroupName = () => {
        viewGroup(groupId).then((result) => {
            setGroupName(result?.data?.name);
            setHasTests(result?.data?.hasTests);
            setHasReport(result?.data?.hasReport);
            setHasProgress(result?.data?.hasProgress);
            setHasPlan(result?.data?.hasPlan);
        });
    }

    useEffect(() => {
        loadGroupName();
        viewDocuments();
    }, []);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/groups" />
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{groupName}</Title>
                <Breadcrumps>
                    <Link title={t('groups.title')} icon="fa-user-group"/>
                    <Link title={t('documents')} isActive={true}/>
                </Breadcrumps>
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        document: null,
                    }}
                >
                    {({ values, handleChange, setFieldValue }) => (
                        <Form>
                        <Row>
                            <Field isFullWidth={true}>
                                <Document
                                    name="document"
                                    onChange={(event) => {
                                        const file = event.currentTarget.files[0];
                                        setFieldValue("document", file);
                                        addDocument({ file, groupId }).then((result) => {
                                            if (result?.error) {
                                                toast.error(result.error.data.error.responseMessage);
                                            } else {
                                                toast.info(result?.data?.info?.responseMessage);
                                                listDocuments(groupId).then((result) => {
                                                    const documents = result?.data?.records;
                                                    setDocuments(documents);
                                                });
                                            }
                                        });
                                    }}
                                />
                            </Field>
                        </Row>
                        <Row>
                            <Field isFullWidth={true}>
                                <DocumentsWrapper>
                                    {documents?.length > 0 &&
                                        documents?.map((document, index) => {
                                            return (
                                                <Item
                                                    id={document.id}
                                                    key={index}
                                                    name={document.fileName}
                                                    type={document.mimeType}
                                                    onDelete={(event) => onDocumentDelete(event, document.id)}
                                                    onDownload={(event) => onDocumentDownload(event, document.id)}
                                                />
                                            );
                                        })}
                                </DocumentsWrapper>
                            </Field>
                        </Row>
                    </Form>
                    )}
                </Formik>
            </Main>
        </DefaultLayout>
    );
};

export default Documents;
